import React, {useContext, useState} from "react";
import {Button, Form, Input, Typography} from "@bezlimit/bezlimit-ui";
import {observer} from "mobx-react";
import _ from "lodash";
import "./style.scss";
// core
import {InterceptorContext, ModalContext} from "../../../core/Contexts";
// hooks
import useFormHandler from "../../../core/hooks/useFormHandler";
import useLockBodyScroll from "../../../core/hooks/useLockBodyScroll";
import SwipeToClose from "../../blocks/ui/swipeToClose";
// services
import ReservationsService from "../../../services/ReservationsService";
// components
import {AttachEsimSuccess, AttachEsimError} from "./result";
import "./result.scss";
// images
import esimImg from "../../../images/esim_card.png";

const ChevronIcon = () => (
    <svg
        className="collapse__arrow"
        width="14"
        height="9"
        viewBox="0 0 14 9"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
    >
        <path
            d="M1 1.5L7 7.5L13 1.5"
            stroke="#1F1F1F"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
);

const formatPhone = (phone: number | string) => {
    const digits = String(phone).replace(/\D/g, "").slice(-10);
    if (digits.length !== 10) return String(phone);
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8, 10)}`;
};

const validateEmail = (v: string) => {
    if (!v) return "Поле обязательно для заполнения";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v))
        return "Указан неверный формат электронной почты";
    return "";
};

export default React.memo(
    observer(function AttachToEsimModal({phone, id}: IProps) {
        const contextModal = useContext(ModalContext);
        const context = useContext(InterceptorContext);
        const [form] = Form.useForm();
        const formInstance = useFormHandler(form);
        useLockBodyScroll();
        const [loading, setLoading] = useState(false);
        const [collapseOpen, setCollapseOpen] = useState(false);
        const [emailValue, setEmailValue] = useState("");
        const [emailTouched, setEmailTouched] = useState(false);

        const emailErr = emailTouched ? validateEmail(emailValue) : "";
        const formValid = !emailErr && !!_.size(_.trim(emailValue));

        const onSave = async () => {
            try {
                setLoading(true);
                await ReservationsService.putReservation({
                    id,
                    additional_data: JSON.stringify({
                        delivery: {
                            email: emailValue.trim(),
                            type: "esim"
                        }
                    })
                } as any);
                setLoading(false);
                contextModal?.setCssClass(" bl-modal-attach-esim-success");
                contextModal?.setChildren(<AttachEsimSuccess />);
                contextModal?.setVisible(true);
                if (context?.setLoading) context.setLoading(true);
            } catch (e) {
                setLoading(false);
                contextModal?.setCssClass(" bl-modal-attach-esim-error");
                contextModal?.setChildren(<AttachEsimError />);
                contextModal?.setVisible(true);
            }
        };

        const onCloseModal = () => {
            contextModal?.setVisible(false);
            contextModal?.setCssClass("");
        };

        return (
            <SwipeToClose onClose={onCloseModal}>
                <Typography as="div" className="bl-attach-to-esim-modal">
                    <div className="bl-attach-to-esim-modal__card">
                        <div className="header">
                            <img src={esimImg} alt="eSIM" className="header__img" />
                            <div className="header__title">
                                <div>Прикрепление номера</div>
                                <div className="header__title-line2">
                                    <span className="header__title-phone">
                                        {formatPhone(phone)}
                                    </span>
                                    {" к eSIM"}
                                </div>
                            </div>
                        </div>
                        <div className="subtitle">
                            Инструкция по самостоятельной установке eSIM придет на указанную
                            почту. Убедитесь, что устройство поддерживает eSIM
                        </div>

                        <Form
                            form={formInstance}
                            layout="vertical"
                            autoComplete="off"
                            className="form"
                            onFinish={onSave}
                        >
                            <div
                                className={`collapse${collapseOpen ? " collapse--open" : ""}`}
                            >
                                <div
                                    className="collapse__header"
                                    onClick={() => setCollapseOpen((v) => !v)}
                                >
                                    <span>Как оформить eSIM</span>
                                    <ChevronIcon />
                                </div>
                                {collapseOpen && (
                                    <div className="collapse__body">
                                        <p>
                                            Замени пластиковую SIM на eSIM или перенеси eSIM на
                                            новое устройство — все не выходя из дома!
                                        </p>
                                        <p>
                                            Подключись к Wi-Fi: во время замены действующая SIM
                                            отключается от сети.
                                        </p>
                                        <p>
                                            После замены в течение 24 часов входящие SMS от
                                            банков и финансовых сервисов будут приостановлены —
                                            это ради безопасности.
                                        </p>
                                        <p className="collapse__warn">
                                            Важно: установи новую eSIM в течение 6 часов после
                                            оформления замены.
                                        </p>
                                        <p>
                                            Если не успеешь, установка станет недоступна — нужно
                                            будет оформить замену eSIM повторно и получить новый
                                            QR-код.
                                            <br />
                                            Отсчет времени начинается с момента выполнения замены
                                            eSIM.
                                        </p>
                                        <p>Убедись, что смартфон поддерживает eSIM.</p>
                                        <p>
                                            Укажи адрес электронной почты — инструкция по
                                            установке eSIM придет туда.
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className={`field${emailErr ? " field--error" : ""}`}>
                                <Input
                                    placeholder="Электронная почта"
                                    value={emailValue}
                                    onChange={(e: any) => {
                                        setEmailValue(e.target.value);
                                        if (!emailTouched) setEmailTouched(true);
                                    }}
                                    onBlur={() => {
                                        if (emailValue) setEmailTouched(true);
                                    }}
                                    autoComplete="new-password"
                                    name="esim-email-no-autofill"
                                    role="presentation"
                                />
                                {emailErr && <div className="field__error">{emailErr}</div>}
                            </div>
                        </Form>
                    </div>

                    <Button
                        type={formValid ? "primary" : "default"}
                        disabled={!formValid}
                        loading={loading}
                        className="save-btn"
                        onClick={onSave}
                    >
                        Сохранить
                    </Button>
                </Typography>
            </SwipeToClose>
        );
    })
);

interface IProps {
    phone: number;
    id: number;
}
